import "./style.css";
import { createStage } from "./render/stage";
import { Scene } from "./render/scene";
import { EditStore } from "./game/editStore";
import { TimeAnimator } from "./game/animator";
import { SessionLog } from "./game/log";
import { HORIZON, type DraggableId, type Placement, type SystemPhase } from "./game/types";
import { stateAt } from "./game/world";
import { Sfx } from "./audio/sfx";
import { CuckooVoice, type CuckooLineId } from "./voice/cuckoo";
import { ElevenLabsRecognizer } from "./voice/elevenLabsRecognizer";
import { WebSpeechRecognizer } from "./voice/webSpeechRecognizer";
import type { RecognitionResult, SpeechRecognizer, SpeechRecognizerEvents } from "./voice/types";
import { DevPanel } from "./devPanel";
import linesJson from "../voice/lines.json";

const LINE_TEXT: Record<string, string> = Object.fromEntries(
  (linesJson.clips as { id: string; text: string }[]).map((c) => [c.id, c.text]),
);

const STORE_KEY = "tiktak.session";

async function boot() {
  const mount = document.getElementById("app")!;
  const stage = await createStage(mount);

  const log = new SessionLog();
  log.add("session_start", { ua: navigator.userAgent });

  const edits = new EditStore();
  const sfx = new Sfx();

  // --- Полудуплекс: прокси к активному распознавателю ---
  let recognizer: SpeechRecognizer | null = null;
  const recProxy = {
    mute: () => recognizer?.mute(),
    unmute: () => recognizer?.unmute(),
  };

  let phase: SystemPhase = "FROZEN";
  const setPhase = (p: SystemPhase) => {
    if (p === phase) return;
    phase = p;
    log.add("phase", { phase: p });
    dev.set("phase", p);
  };

  const cuckoo = new CuckooVoice(recProxy, log, (speaking) => {
    if (speaking) setPhase("SPEAKING");
    else setPhase(animator.moving ? "MOVING" : "FROZEN");
    scene.showCuckoo(speaking);
  });

  const say = (id: CuckooLineId, opts: { force?: boolean } = {}) => {
    scene.cuckooSay(LINE_TEXT[id] || "");
    return cuckoo.say(id, LINE_TEXT[id] || "", opts);
  };

  // --- Прогресс/подсказки ---
  let started = false;
  let reachedTenOnce = false;
  let earlyHatchCount = 0;
  let missStreak = 0;
  let eggEverTouched = false;
  let eggColdHinted = false;
  let lastActivityAt = Date.now();
  let idlePromptAt = 0;
  const sessionStart = Date.now();
  let bufferedNumbers: number[] = [];

  const persist = () => {
    try {
      localStorage.setItem(
        STORE_KEY,
        JSON.stringify({
          t: animator.t,
          edits: edits.list(),
          reachedTenOnce,
          earlyHatchCount,
          savedAt: new Date().toISOString(),
        }),
      );
    } catch {
      /* приватный режим — просто не сохраняем */
    }
  };

  // --- Аниматор времени ---
  const animator = new TimeAnimator(edits, {
    onMoveStart: (_from, _to, dir) => {
      setPhase("MOVING");
      scene.setFrozen(null);
      scene.setRewinding(dir < 0);
    },
    onMomentCross: (m, dir) => {
      scene.setRewinding(dir < 0);
      const st = stateAt(edits.list(), m);
      for (const ev of st.events) sfx.play(ev.kind, dir < 0);
      // эхо числа (обратное — при счёте назад)
      void cuckoo.echo(m, dir < 0);

      if (dir > 0 && m === HORIZON && !reachedTenOnce) {
        reachedTenOnce = true;
        window.setTimeout(() => say("cuckoo_night", { force: true }), 400);
        window.setTimeout(() => say("want_frog", { force: true }), 3200);
      }
      // ранний цыплёнок: мама возвращается на 8
      if (st.events.some((e) => e.kind === "eggHatch") && m < 8) {
        earlyHatchCount++;
        if (earlyHatchCount === 1) window.setTimeout(() => say("too_early", { force: true }), 500);
        else if (earlyHatchCount === 2) window.setTimeout(() => say("mom_at_eight", { force: true }), 500);
      }
      persist();
    },
    onFreeze: (m) => {
      setPhase("FROZEN");
      scene.setFrozen(m);
      scene.setRewinding(false);
      idlePromptAt = Date.now();
      persist();
    },
    onRepeatShake: (_m, reps) => {
      sfx.play("tick");
      if (reps >= 3) say("giggle", { force: true });
    },
    onOverHorizon: () => say("only_tomorrow"),
    onEditsUndone: () => sfx.rewind(),
  }, log);

  // --- Сцена ---
  const scene = new Scene(edits, {
    onDragStart: (id) => {
      log.add("drag_start", { id });
      if (id === "egg") eggEverTouched = true;
    },
    onEdit: (objectId: DraggableId, tick: number, placement: Placement) => {
      edits.set(objectId, tick, placement);
      log.add("edit_created", { objectId, tick, placement });
      log.add("drag_drop", { objectId, tick });
      lastActivityAt = Date.now();
      if (bufferedNumbers.length) {
        animator.enqueue(bufferedNumbers);
        bufferedNumbers = [];
      }
      persist();
    },
  });
  stage.root.addChild(scene.container);

  // --- События распознавания ---
  const recEvents: SpeechRecognizerEvents = {
    onStatus: (s) => dev.set("phase", `${phase} · rec:${s}`),
    onSpeechStart: (at) => {
      log.add("speech_start", { at });
      lastActivityAt = Date.now();
    },
    onSpeechEnd: (at) => {
      log.add("speech_end", { at });
      if (!animator.moving) setPhase("PROCESSING");
    },
    onError: (msg) => {
      log.add("stt_error", { msg });
    },
    onResult: (res: RecognitionResult) => {
      log.add("raw_transcript", { text: res.rawText, confidence: res.confidence, sttMs: res.sttMs });
      dev.set("raw", res.rawText || "—");
      dev.set("conf", res.confidence != null ? res.confidence.toFixed(2) : "—");
      dev.set("stt", res.sttMs != null ? String(res.sttMs) : "—");

      if (res.numbers.length === 0) {
        log.add("no_number", { text: res.rawText });
        missStreak++;
        if (missStreak >= 3) {
          missStreak = 0;
          say("louder", { force: true });
        }
        if (!animator.moving) setPhase("FROZEN");
        updateCounts();
        return;
      }
      missStreak = 0;
      log.add("number_extracted", { numbers: res.numbers });
      dev.set("num", res.numbers.join(", "));

      const reactionAt = Date.now();
      log.addLatency({
        speechEnd: res.endedAt,
        sttMs: res.sttMs ?? 0,
        numberAt: res.recognizedAt,
        reactionAt,
        total: reactionAt - res.endedAt,
      });
      const m = log.metrics();
      dev.set("total", String(reactionAt - res.endedAt));
      dev.set("med", m.medianLatencyMs != null ? String(m.medianLatencyMs) : "—");
      dev.set("p95", m.p95LatencyMs != null ? String(m.p95LatencyMs) : "—");

      lastActivityAt = Date.now();
      if (scene.isDragging) bufferedNumbers.push(...res.numbers);
      else animator.enqueue(res.numbers);
      updateCounts();
    },
  };

  function updateCounts() {
    const m = log.metrics();
    dev.set("cnt", `${m.utterances}/${m.numbersExtracted}/${m.missedNumbers}`);
  }

  // --- Панель фасилитатора ---
  let sttEnabled = true;
  const dev = new DevPanel(
    {
      toggleStt: (on) => {
        sttEnabled = on;
        if (on) void startRecognizer();
        else recognizer?.stop();
      },
      switchRecognizer: async (name) => {
        recognizer?.stop();
        recognizer = name === "webspeech" ? new WebSpeechRecognizer(recEvents) : new ElevenLabsRecognizer(recEvents);
        if (started && sttEnabled) await startRecognizer();
      },
      jumpToMoment: (n) => {
        log.add("jump", { to: n, source: "dev" });
        animator.jumpTo(n);
      },
      resetWorld: () => {
        edits.reset();
        animator.reset();
        reachedTenOnce = false;
        earlyHatchCount = 0;
        bufferedNumbers = [];
        localStorage.removeItem(STORE_KEY);
        scene.setFrozen(0);
      },
      forceCuckoo: () => say("more", { force: true }),
      exportLog: () => {
        const blob = new Blob([JSON.stringify(log.export(), null, 2)], { type: "application/json" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `tiktak-log-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
      },
      postLog: async () => {
        try {
          const r = await fetch("/api/log", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(log.export()),
          });
          alert(r.ok ? "Лог отправлен" : `Ошибка ${r.status}`);
        } catch (e) {
          alert("Не удалось отправить лог");
        }
      },
    },
    log,
  );

  recognizer = new ElevenLabsRecognizer(recEvents);

  async function startRecognizer() {
    if (!recognizer || !sttEnabled) return;
    try {
      await recognizer.start();
    } catch (e) {
      log.add("speech_error", { msg: String((e as Error).message || e) });
    }
  }

  // --- Восстановление сессии ---
  try {
    const saved = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
    if (saved && Array.isArray(saved.edits)) {
      for (const e of saved.edits) edits.set(e.objectId, e.tick, e.placement);
      animator.t = Math.max(0, Math.min(HORIZON, Math.round(saved.t || 0)));
      reachedTenOnce = !!saved.reachedTenOnce;
      earlyHatchCount = saved.earlyHatchCount || 0;
      scene.setFrozen(animator.t);
    }
  } catch {
    /* нет сохранённого состояния */
  }

  // --- Дверца часов: единственное касание, запускает мир ---
  const overlay = document.createElement("div");
  overlay.id = "door-overlay";
  overlay.innerHTML = `<div id="door">🕰️</div>`;
  document.body.appendChild(overlay);

  overlay.addEventListener(
    "pointerup",
    async () => {
      if (started) return;
      started = true;
      overlay.classList.add("gone");
      setTimeout(() => overlay.remove(), 700);

      await sfx.unlock();
      cuckoo.unlock();
      requestWakeLock();
      await startRecognizer();

      scene.showCuckoo(true);
      await say("hush", { force: true });
      window.setTimeout(() => {
        scene.showCuckoo(false);
        void say("raz", { force: true });
      }, 1400);
      lastActivityAt = Date.now();
      idlePromptAt = Date.now();
    },
    { once: false },
  );

  // --- Wake Lock ---
  let wakeLock: any = null;
  async function requestWakeLock() {
    try {
      wakeLock = await (navigator as any).wakeLock?.request("screen");
    } catch {
      /* не поддерживается */
    }
  }
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && started) {
      requestWakeLock();
    } else {
      wakeLock?.release?.().catch(() => {});
      wakeLock = null;
    }
  });

  // --- Главный цикл ---
  let last = performance.now();
  stage.app.ticker.add(() => {
    const now = performance.now();
    const dt = Math.min(100, now - last);
    last = now;

    animator.update(dt);
    scene.renderAt(animator.t, now);
    dev.set("t", animator.t.toFixed(2));

    // Подсказки в заморозке (GDD 4.7). Только пока ребёнок не дошёл до 10 сам.
    if (started && phase === "FROZEN" && !cuckoo.isSpeaking) {
      const silent = Date.now() - lastActivityAt;
      const sinceStart = Date.now() - sessionStart;
      const fm = animator.frozenMoment;

      if (!reachedTenOnce && sinceStart < 180_000 && silent > 6000 && Date.now() - idlePromptAt > 6000 && fm !== null) {
        idlePromptAt = Date.now();
        if (fm === 0 && silent > 15000) {
          animator.jumpTo(1);
          window.setTimeout(() => say("now_you_two", { force: true }), 700);
        } else {
          void cuckoo.echo(Math.min(HORIZON, fm + 1)); // шёпот следующего числа
        }
      }

      if (sinceStart > 240_000 && !eggEverTouched && !eggColdHinted && fm !== null) {
        eggColdHinted = true;
        void say("egg_cold", { force: true });
      }
    }
  });

  window.addEventListener("pagehide", persist);
  window.addEventListener("beforeunload", persist);

  // Первый кадр — спящий мир.
  scene.renderAt(animator.t, performance.now());
}

boot().catch((e) => {
  console.error(e);
  document.body.innerHTML = `<pre style="padding:20px;color:#fff">Ошибка запуска: ${String(e)}</pre>`;
});
